import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, CheckCheck, GitBranch, Receipt, MessageSquare, Reply, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Notification renderer.
 *
 * After the optimization pass we only generate three types of notifications:
 *   • post_update   — publisher updates a game (in-app only)
 *   • payment_proof — admin-only ping when a user submits payment proof
 *   • (legacy)      — comment/reply notifications no longer generated; rendered for back-compat
 */
function NotifIcon({ type }) {
  const cls = "w-4 h-4";
  if (type === "post_update") return <span className="w-9 h-9 rounded-full grid place-items-center bg-emerald-900/40 text-emerald-300"><GitBranch className={cls} /></span>;
  if (type === "payment_proof") return <span className="w-9 h-9 rounded-full grid place-items-center bg-amber-900/40 text-amber-300"><Receipt className={cls} /></span>;
  if (type === "reply") return <span className="w-9 h-9 rounded-full grid place-items-center bg-violet-900/40 text-violet-300"><Reply className={cls} /></span>;
  if (type === "comment") return <span className="w-9 h-9 rounded-full grid place-items-center bg-rose-900/40 text-rose-300"><MessageSquare className={cls} /></span>;
  return <span className="w-9 h-9 rounded-full grid place-items-center bg-zinc-800 text-zinc-300"><Sparkles className={cls} /></span>;
}

function NotifBody({ n }) {
  const p = n.payload || {};
  if (n.type === "post_update") {
    return (
      <>
        <div className="text-sm text-zinc-200">
          <span className="font-bold text-emerald-300">{p.message || `${p.post_title || "A game"} was updated`}</span>
        </div>
        {p.version && <div className="text-xs text-zinc-400 mt-0.5">Version: <span className="text-emerald-200 font-mono">v{p.version}</span></div>}
      </>
    );
  }
  if (n.type === "payment_proof") {
    return (
      <>
        <div className="text-sm text-zinc-200">
          <span className="font-bold">New payment proof submitted by {p.user_name || "a cultist"}</span>
        </div>
        {p.tier && <div className="text-xs text-zinc-400 mt-0.5">Tier: <span className="text-amber-200">{p.tier.replace(/_/g, " ")}</span>{p.amount ? ` · ${p.amount}` : ""}</div>}
      </>
    );
  }
  // Legacy comment / reply rendering (kept for back-compat with old records)
  return (
    <>
      <div className="text-sm text-zinc-200">
        <span className="font-bold">{p.actor_name}</span>{" "}
        {n.type === "reply" ? "replied to your comment" : "commented on"}{" "}
        <span className="text-rose-300">{p.post_title}</span>
      </div>
      {p.snippet && (<div className="text-xs text-zinc-400 mt-1 line-clamp-2 italic">&ldquo;{p.snippet}&rdquo;</div>)}
    </>
  );
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 100 } });
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user && user !== false) load();
    else if (user === false) nav("/login?next=/notifications");
  }, [user, load, nav]);

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      await load();
      toast.success("All marked as read");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const open = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
    }
    // Route to a sensible destination per type
    if (n.type === "payment_proof") { nav("/admin/payments"); return; }
    const slug = n.payload?.post_slug;
    if (slug) nav(`/posts/${slug}`);
  };

  return (
    <PageShell>
      <section className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-black text-white inline-flex items-center gap-2">
              <Bell className="w-6 h-6 text-rose-400" /> Notifications
              {unread > 0 && <span className="text-sm text-rose-300 font-mystic">({unread} unread)</span>}
            </h1>
          </div>
          {items.some((i) => !i.read) && (
            <Button variant="outline" onClick={markAll} className="border-rose-500/40 text-rose-300" data-testid="mark-all-read-btn">
              <CheckCheck className="w-4 h-4 mr-2" /> Mark all read
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mystic text-center py-12">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <BellOff className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <div className="text-zinc-500 font-mystic italic">No notifications yet.</div>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="notifications-list">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  onClick={() => open(n)}
                  className={`w-full text-left glass rounded-xl p-4 hover:bg-white/5 transition ${!n.read ? "border-rose-500/40" : ""}`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    <NotifIcon type={n.type} />
                    <div className="flex-1 min-w-0">
                      <NotifBody n={n} />
                      <div className="text-[10px] text-zinc-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(225,29,72,0.7)] mt-2" />}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </PageShell>
  );
}
